import { useEffect, useState } from "react";
import { cleanSlides, DEFAULT_SLIDES } from "@/components/HeroSlider";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Loader2, Image as ImageIcon, Plus, Trash2, ChevronLeft, ChevronRight, Eye } from "lucide-react";

/**
 * Side drawer for admins that lets them edit ALL hero slides inline on the home page.
 * Saves to /api/admin/site-config and fires a `hero-updated` event so the HeroSlider re-fetches.
 */
export default function HeroInlineEditor({ open, onClose }) {
  const [slides, setSlides] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otherCfg, setOtherCfg] = useState({});

  useEffect(() => {
    if (!open) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    setLoading(true);
    api.get("/site-config").then((r) => {
      const cfg = r.data || {};
      setOtherCfg({ categories: cfg.categories || [], testimonials: cfg.testimonials || [] });
      // Same slides the home actually shows (removed/empty config falls back to defaults)
      const cleaned = cleanSlides(cfg.hero);
      const effective = cleaned.length ? cleaned : DEFAULT_SLIDES;
      setSlides(effective);
      // Pre-select the slide currently visible on the hero (if any)
      const idx = Number(document.querySelector("[data-hero-current]")?.dataset?.heroCurrent || 0);
      setActive(Math.max(0, Math.min(idx, effective.length - 1)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [open]);

  const [peek, setPeek] = useState(false);

  // Live preview: every change is mirrored on the home hero (not saved until "Salva")
  useEffect(() => {
    if (!open || loading || !slides.length) return;
    window.dispatchEvent(new CustomEvent("hero-preview", { detail: { slides, active } }));
  }, [open, loading, slides, active]);

  const close = () => {
    window.dispatchEvent(new CustomEvent("hero-preview-end"));
    onClose();
  };

  useEffect(() => {
    if (!peek) return;
    const stop = () => setPeek(false);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("blur", stop);
    };
  }, [peek]);

  const emptySlide = () => ({
    overline: "",
    title: "",
    subtitle: "",
    cta: "Scopri di più",
    href: "/catalogo",
    img: "",
    kenburns: "zoom",
  });

  const updateActive = (patch) => {
    setSlides((prev) => prev.map((s, i) => (i === active ? { ...s, ...patch } : s)));
  };

  const addSlide = () => {
    setSlides((prev) => [...prev, emptySlide()]);
    setActive(slides.length);
  };
  const removeSlide = () => {
    if (slides.length <= 1) return;
    const next = slides.filter((_, i) => i !== active);
    setSlides(next);
    setActive(Math.max(0, active - 1));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-config", { hero: slides, ...otherCfg });
      toast.success("Hero aggiornato — pubblicato subito");
      window.dispatchEvent(new CustomEvent("hero-updated"));
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && !/\.(heic|heif|avif|svg)$/i.test(file.name || "")) {
      toast.error("Seleziona un'immagine (jpg, png, webp, heic, avif, svg)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      // NOTE: DO NOT set Content-Type manually — axios will add the multipart boundary.
      const r = await api.post("/admin/upload/image", fd);
      updateActive({ img: r.data.url });
      toast.success("Immagine caricata");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Errore sconosciuto";
      toast.error(`Upload fallito: ${detail}`);
    }
  };

  if (!open) return null;
  const s = slides[active] || emptySlide();

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="hero-editor-backdrop"
        onClick={close}
        className={`fixed inset-0 bg-black/10 z-[70] transition-opacity ${peek ? "opacity-0" : ""}`}
      />
      {/* Drawer */}
      <aside
        data-testid="hero-inline-editor"
        className={`light-panel fixed top-0 right-0 h-full w-full max-w-[520px] bg-white shadow-2xl z-[71] flex flex-col border-l border-black/10 transition-opacity duration-200 ${peek ? "opacity-0 pointer-events-none" : ""}`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#22ff66] font-bold">Editor rapido</div>
            <h2 className="heading-uc text-2xl">Modifica Hero</h2>
          </div>
          <button
            data-testid="hero-editor-close"
            onClick={close}
            className="w-9 h-9 border border-black/10 hover:bg-black/5 flex items-center justify-center"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-black/50">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Slide switcher */}
            <div className="px-6 py-3 border-b border-black/10 flex items-center justify-between bg-black/[0.02]">
              <div className="flex items-center gap-2">
                <button
                  data-testid="hero-editor-prev"
                  onClick={() => setActive((i) => Math.max(0, i - 1))}
                  disabled={active === 0}
                  className="w-8 h-8 border border-black/10 disabled:opacity-30 hover:bg-black/5 flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="text-xs uppercase tracking-widest text-black/60 font-semibold">
                  Slide {active + 1} / {slides.length}
                </div>
                <button
                  data-testid="hero-editor-next"
                  onClick={() => setActive((i) => Math.min(slides.length - 1, i + 1))}
                  disabled={active >= slides.length - 1}
                  className="w-8 h-8 border border-black/10 disabled:opacity-30 hover:bg-black/5 flex items-center justify-center"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="hero-editor-add"
                  onClick={addSlide}
                  className="flex items-center gap-1 text-xs uppercase tracking-widest border border-black/15 px-3 h-8 hover:bg-black/5"
                >
                  <Plus className="w-3.5 h-3.5" /> Aggiungi
                </button>
                <button
                  data-testid="hero-editor-remove"
                  onClick={removeSlide}
                  disabled={slides.length <= 1}
                  className="flex items-center gap-1 text-xs uppercase tracking-widest border border-[#22ff66]/40 text-[#22ff66] px-3 h-8 hover:bg-[#22ff66]/10 disabled:opacity-30"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Rimuovi
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <Field label="Overline (piccolo sopra il titolo)">
                <input
                  data-testid="hero-field-overline"
                  value={s.overline || ""}
                  onChange={(e) => updateActive({ overline: e.target.value })}
                  placeholder="Es. SUV Lungo Termine"
                  className="w-full h-11 border border-black/15 px-3 text-sm outline-none focus:border-[#22ff66]"
                />
              </Field>

              <Field label="Titolo (usa ↵ per andare a capo)">
                <textarea
                  data-testid="hero-field-title"
                  value={s.title || ""}
                  onChange={(e) => updateActive({ title: e.target.value })}
                  rows={3}
                  placeholder="AUDI Q5.&#10;LA TUA NUOVA QUOTIDIANITÀ."
                  className="w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#22ff66] resize-none"
                />
              </Field>

              <Field label="Sottotitolo">
                <textarea
                  data-testid="hero-field-subtitle"
                  value={s.subtitle || ""}
                  onChange={(e) => updateActive({ subtitle: e.target.value })}
                  rows={3}
                  className="w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#22ff66] resize-none"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Testo pulsante">
                  <input
                    data-testid="hero-field-cta"
                    value={s.cta || ""}
                    onChange={(e) => updateActive({ cta: e.target.value })}
                    placeholder="Scopri di più"
                    className="w-full h-11 border border-black/15 px-3 text-sm outline-none focus:border-[#22ff66]"
                  />
                </Field>
                <Field label="Link pulsante">
                  <input
                    data-testid="hero-field-href"
                    value={s.href || ""}
                    onChange={(e) => updateActive({ href: e.target.value })}
                    placeholder="/catalogo"
                    className="w-full h-11 border border-black/15 px-3 text-sm outline-none focus:border-[#22ff66]"
                  />
                </Field>
              </div>

              <Field label="Immagine di sfondo (URL)">
                <div className="flex gap-2">
                  <input
                    data-testid="hero-field-img"
                    value={s.img || ""}
                    onChange={(e) => updateActive({ img: e.target.value })}
                    placeholder="https://..."
                    className="flex-1 h-11 border border-black/15 px-3 text-sm outline-none focus:border-[#22ff66]"
                  />
                  <label className="h-11 px-4 border border-black/15 hover:bg-black/5 flex items-center gap-2 text-xs uppercase tracking-widest cursor-pointer">
                    <ImageIcon className="w-4 h-4" /> Carica
                    <input
                      data-testid="hero-field-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => uploadImage(e.target.files?.[0])}
                      className="hidden"
                    />
                  </label>
                </div>
                {s.img && (
                  <img
                    src={s.img}
                    alt="anteprima"
                    className="mt-3 w-full aspect-[16/9] object-cover border border-black/10"
                  />
                )}
              </Field>
            </div>

            {/* Footer save */}
            <div className="p-4 border-t border-black/10 flex items-center gap-3 bg-white sticky bottom-0">
              <button
                data-testid="hero-editor-cancel"
                onClick={close}
                className="flex-1 h-12 border border-black/15 hover:bg-black/5 uppercase tracking-widest text-xs font-semibold"
              >
                Annulla
              </button>
              <button
                data-testid="hero-editor-save"
                onClick={save}
                disabled={saving}
                className="flex-1 h-12 bg-[#22ff66] hover:bg-[#4EFF87] text-black uppercase tracking-widest text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salva Rapido
              </button>
            </div>
          </>
        )}
      </aside>
      {!loading && (
        <button
          type="button"
          data-testid="hero-editor-peek"
          onPointerDown={(e) => { e.preventDefault(); setPeek(true); }}
          onContextMenu={(e) => e.preventDefault()}
          className={`md:hidden fixed left-1/2 -translate-x-1/2 bottom-24 z-[72] select-none touch-none inline-flex items-center gap-2 px-5 h-11 rounded-full shadow-2xl text-xs uppercase tracking-widest font-bold transition-colors ${peek ? "bg-[#22ff66] text-black" : "bg-black text-white"}`}
        >
          <Eye className="w-4 h-4" /> {peek ? "Rilascia per tornare" : "Tieni premuto: anteprima"}
        </button>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-black/60 font-semibold mb-1.5">{label}</div>
      {children}
    </div>
  );
}
